import { ROLE_NAMES } from "src/consts/role";
import { sanitizeObjectStringValues } from "src/helpers/security";
import { INACTIVE_TRIAL_INFO } from "src/consts/trial";
import request from "../request-manager";

const prefixURL = "/user";
const customHead = { "content-type": "multipart/form-data" };

export type InfoUserResponse = {
  actions_rule: { message: keyof typeof ROLE_NAMES };
  allowed_create_transaction: boolean;
  company_has_maker: boolean;
  current_user: CurrentUser;
  user_owner: CurrentUser;
  company: Company;
  company_sandbox: { deposit: number; max_pwf_limit_amount: number };
  id: number;
  owner_id: number;
  tfa_status: "inactive" | "active";
  username: string;
  is_mt_transaction_restricted: boolean;
  is_ap_transaction_restricted: boolean;
  is_it_transaction_restricted: boolean;
};

export type VerificationInfoResponse = {
  id: number;
  email: string;
  username: string;
  owner_id: number;
  role: string;
  job_title: null;
  job_function: null;
  actions_rule: {
    code: string;
    message: string;
  };
  allowed_create_transaction: boolean;
  company_has_maker: boolean;
  user_owner: UserOwnerVerification;
  company: CompanyVerification;
  company_sandbox: {
    deposit: number;
    max_pwf_limit_amount: number;
  };
  is_automate_partnership_agreement: boolean;
  verification_step: string;
};

export type VerificationsResponse = {
  id: number;
  kyb: number;
  name: string;
  online_platform_channel: string;
  selling_platform: string;
  ultimate_beneficiary: string;
};

export type CompanyVerification = {
  id: number;
  owner_id: number;
  deposit: number;
  company_id: string;
  email: string;
  phone_number: string;
  name_of_business: string;
  form_of_business: string;
  license_number: null;
  province: string;
  city: string;
  postal_code: string;
  address: null;
  npwp: null;
  package: number;
  identity_verifications_status: number;
  version: number;
  display_name: string;
  logo: string;
  cs_email: string;
  email_report_type: number;
  account_type: number;
  industry: number;
  predic_trx_freq: number;
  predic_trx_volume: number;
  predic_trx_method: null;
  siup: string;
  deed_of_incorporation: string;
  sk_kemenkumham: string;
  company_npwp: null;
  nib: null;
  deed_of_amandment_articles_association: null;
  deed_of_change_management: null;
  additional_doc: null;
  pks: null;
  power_of_attorney: null;
  api_status: number;
  kyc_status: number;
  payment_method: {
    code: number;
    message: string;
  };
  bank_clusters: null;
  deposit_temporary: number;
  deposit_limit: number;
  deposit_notification: number;
  deposit_notification_emails: string;
  lang: string;
  max_transaction_amount: number;
  deposit_status: number;
  pwf_status: number;
  bank: string;
  account_number: string;
  bank_account_name: string;
  bank_branch: string;
  product_type: string;
  service_type: Record<string, string>;
  contact_name: string;
  purpose_of_use: string;
  business_id_number: string;
};

export interface UserOwnerVerification {
  id: number;
  email: string;
  email_verified: boolean;
  name: string;
  identity_number: string;
  identity_type: string;
  birth_date: null;
  birth_place: {
    code: number;
    name: string;
  };
  address: string;
  city: {
    code: number;
    name: string;
  };
  phone_number: string;
  phone_number_verified: boolean;
  identity_image: null;
  face_and_identity_image: null;
  deposit: number;
  saving: number;
  created_source: string;
  created_from: null;
  identity_verifications_status: number;
  job: null;
  is_new_user: boolean;
  is_company_pic: boolean;
  letter_of_attorney: null;
}

export type Company = {
  id: number;
  deposit: number;
  company_id: string;
  display_name?: string;
  owner_id: number;
  email: string;
  bank: string;
  account_number: string;
  form_of_business: string;
  phone_number: string;
  name_of_business: string;
  is_auto_split: boolean;
  package: number;
  logo: string;
  api_status: number;
  payment_method: { code: number };
  max_transaction_amount: number;
  deposit_status: number;
  globe_status: number;
  pwf_status: number;
  max_pwf_limit_amount: number;
  kyc_status: number;
  industry: number;
  account_type: number;
  risk_level: string;
};

export type CurrentUser = {
  id: number;
  email: string;
  email_verified: boolean;
  email_change_requested: string;
  name: string;
  phone_number: string;
  phone_number_verified: boolean;
  identity_verifications_status: number;
  is_new_user: boolean;
  is_request_change_email: boolean;
};

export type TrialInfoResponse = {
  transaction_status: string;
  started_at: string;
  expired_at: string;
  transaction_limit: TransactionLimit;
};

export type TransactionLimit = {
  count: number;
  max_count: number;
  amount: number;
  max_amount: number;
};

const UserAPI = {
  loginUser(data: any, token: string) {
    return request.post(`${prefixURL}/new-login`, data, { Authorization: `Bearer ${token}` });
  },
  oldLoginUser(data: any) {
    return request.post(`${prefixURL}/login`, data);
  },
  authenticateUser(data: any) {
    return request.post(`${prefixURL}/authenticate`, data);
  },
  verifyOTPTrustedDevice(data: any, token: string) {
    return request.post(`${prefixURL}/verify-otp-auth`, data, { Authorization: `Bearer ${token}` });
  },
  getRegisteredCompanies(token: string) {
    return request.get(`${prefixURL}/registered-companies`, {}, { Authorization: `Bearer ${token}` });
  },
  logoutUser(headers = {}) {
    return request.post(`${prefixURL}/logout`, {}, headers);
  },
  checkCredentialUser(data: any) {
    return request.post(`${prefixURL}/check-credential-user`, data);
  },
  checkFFBIDSuggestion(data: any) {
    return request.post(`${prefixURL}/get-ffb-id-suggestions`, data);
  },
  createFFBAccount(data: any) {
    return request.post(`${prefixURL}/create-ffb-account`, data);
  },
  createFlipAccount(data: any) {
    return request.post(`${prefixURL}/create-flip-account`, data);
  },
  createBigFlipAccount(data: any) {
    return request.post(`${prefixURL}/create-big-flip-account`, data);
  },
  updateCompanyData(data: any) {
    return request.put(`${prefixURL}/update-company-data`, sanitizeObjectStringValues(data));
  },
  uploadCompanyImage(data: any) {
    return request.post(`${prefixURL}/upload-company-image`, data, customHead);
  },
  updateUserPersonalData(data: any) {
    return request.put(`${prefixURL}/update-personal-data`, sanitizeObjectStringValues(data));
  },
  uploadIdentityImage(data: any) {
    return request.post(`${prefixURL}/upload-identity-image`, data, customHead);
  },
  verifyUserIdentity() {
    return request.post(`${prefixURL}/verify-user-identity`);
  },
  sendEmailConfirmation(data: any) {
    return request.post(`${prefixURL}/send-email-confirmation`, sanitizeObjectStringValues(data));
  },
  verifyUserEmail(data: any) {
    return request.post(`${prefixURL}/verify-email`, sanitizeObjectStringValues(data));
  },
  sendPhoneNumberVerification(data: any) {
    return request.post(`${prefixURL}/send-phone-number-verification`, data);
  },
  verifyPhoneNumber(data: any) {
    return request.post(`${prefixURL}/verify-phone-number`, data);
  },
  verificationInfo(): Promise<VerificationInfoResponse> {
    return request.get<VerificationInfoResponse>(`${prefixURL}/verification-info`);
  },
  infoUser(): Promise<InfoUserResponse> {
    return request.get<InfoUserResponse>(`${prefixURL}/info`);
  },
  newPassword(data: any) {
    return request.post(`${prefixURL}/new-password`, data);
  },
  verifyOwnerAndCompanyData(data: any) {
    return request.post(`${prefixURL}/update-verification-data`, sanitizeObjectStringValues(data));
  },
  uploadOwnerIdentityImage(data: any) {
    return request.post(`${prefixURL}/upload-owner-identity-image`, data, customHead);
  },
  submitVerification(data: any) {
    return request.post(`${prefixURL}/submit-verification`, data);
  },
  verifyDocument(data: any) {
    return request.post(`${prefixURL}/verify-document`, data);
  },
  getTrialInfo(): Promise<TrialInfoResponse> {
    // Disbursement trial is shut down; resolve a static inactive-trial response instead of calling GET /user/trial-info.
    return Promise.resolve(INACTIVE_TRIAL_INFO);
  },
  sendTrialNotification(data: any) {
    return request.post(`${prefixURL}/send-trial-end-notification`, data);
  },
  resubmitVerification() {
    return request.post(`${prefixURL}/resubmit-verification`);
  },
  verificationUpdateCompanyData(data: any) {
    return request.put("/company/update-company-data", data);
  },
  verificationUpdatePersonalData(data: any) {
    return request.put("/company/update-personal-data", data);
  },
  verificationUpdateDocumentData(data: any) {
    return request.post("/company/update-document-data", data, customHead);
  },
  verificationSubmit(data: any) {
    return request.post("/company/submit-verification", data);
  },
  checkHasPassword(param: any) {
    return request.get(`${prefixURL}/check-password`, param);
  },
  registerExistingAccountNoPassword(data: any) {
    return request.post(`${prefixURL}/register-existing-account`, data);
  },
  sendOTPAuth(data: any, token: string) {
    return request.post(`${prefixURL}/send-otp-auth`, data, { Authorization: `Bearer ${token}` });
  },
  verifyOTPRegistration(data: any, token: string) {
    return request.post(`${prefixURL}/verify-otp-registration`, data, { Authorization: `Bearer ${token}` });
  },
  switchCompany(data: any) {
    return request.post(`${prefixURL}/switch-account`, data);
  },
  getIdentityOCR(data: any) {
    return request.post(`${prefixURL}/get-identity-ocr`, data);
  },
  getForgotPasswordInfo(params: any) {
    return request.get(`${prefixURL}/forgot-password-info`, params);
  },
  sendOTPForgotPassword(data: any) {
    return request.post(`${prefixURL}/send-otp-forgot-password`, data);
  },
  verifyOTPForgotPassword(data: any) {
    return request.post(`${prefixURL}/verify-otp-forgot-password`, data);
  },
  sendEmailForgotPassword(data: any, customHead: any) {
    return request.post(`${prefixURL}/forgot-password`, data, { "accept-language": customHead?.lang });
  },
  resetPassword(data: any) {
    return request.post(`${prefixURL}/reset-password`, data);
  },
  addFFBAccount(data: any) {
    return request.post(`${prefixURL}/add-ffb-account`, data);
  }
};

export default UserAPI;
